import { useEffect, useState, useCallback } from "react";
import {
  handleRedirect, getSession, login, signup, logout, api, getAuthClient, tokenOrRelogin,
  ApiError, AuthSetupError, ReauthError, type Session,
} from "./auth";
import { connectExtension, notifyExtensionSignedOut, type ExtensionStatus } from "./extension";
import { extensionStep } from "./FillHint";
import Preferences from "./Preferences";
import AnswersScreen from "./Answers";
import ResumeScreen from "./Resume";
import Matches from "./Matches";
import Applications from "./Applications";
import type { NextStepInfo } from "./NextStep";
import { updateAvailable, UPDATE_CHECK_MS } from "./update";
import TailorScreen from "./Tailor";
import { Dashboard } from "./Dashboard";
import { Landing } from "./Landing";
import TracksScreen, { TrackSwitcher, type Track } from "./Tracks";
import AutoApply from "./AutoApply";
import AnswerLeft from "./AnswerLeft";
import Settings from "./Settings";
import type { SendRoute } from "./SendTag";
import Guide from "./Guide";
import { SubscribeScreen, takeBillingReturn, type Access } from "./Billing";

type View = "dashboard" | "preferences" | "answers" | "resume" | "matches" | "applications" | "tailor" | "tracks" | "settings" | "auto-apply" | "guide" | "answer-left";

type Stage = "saved" | "applied" | "screening" | "interviewing" | "offer" | "rejected" | "closed";

interface Me {
  sub: string;
  email: string | null;
  isFounder: boolean;
  budget: { dailyRemaining: number; monthlyRemaining: number };
  onboarding: {
    resumeUploaded: boolean;
    preferencesSet: boolean;
    answersConfirmed: boolean;
  };
  pipeline: Record<Stage, number>;
  /** Auto at a glance (E14). */
  auto?: { mode: "auto" | "review"; cloud: boolean; sentToday: number; needsYou: number; browsers: number };
  /** The person's job-type tracks, for the top-bar switcher. */
  tracks?: Track[];
  /** Whether this person may use the product (billing). Absent or not enabled = yes. */
  access?: Access;
}

type Phase = { k: "loading" } | { k: "signed-out" } | { k: "in"; me: Me } | { k: "error"; msg: string };

function describe(err: unknown): string {
  if (err instanceof AuthSetupError) return err.message;
  if (err instanceof ApiError) return `${err.message} (HTTP ${err.status})`;
  if (err instanceof Error) return `${err.name}: ${err.message}`;
  return String(err);
}

export default function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [phase, setPhase] = useState<Phase>({ k: "loading" });
  const [actionError, setActionError] = useState<string | null>(null);
  const [view, setView] = useState<View>("dashboard");
  /** A send-route filter to hand Matches when the dashboard breakdown is clicked; cleared on a plain nav to Matches. */
  const [matchesRoute, setMatchesRoute] = useState<SendRoute | null>(null);
  /** Which posting the tailor screen is for, and where Back should go. */
  const [tailor, setTailor] = useState<{ jobId: string; from: View } | null>(null);
  /** The Chrome extension, if it is installed here and has been handed the sign-in. */
  const [ext, setExt] = useState<ExtensionStatus | null>(null);
  /** The extension sent the person here to connect; say when it has. */
  const [fromExtension] = useState(() => window.location.hash === "#extension");
  // #landing shows the front door to someone already signed in -- for looking at it without signing out.
  const [previewLanding, setPreviewLanding] = useState(() => window.location.hash === "#landing");
  // Back from Stripe Checkout (#billing=success|cancelled): read once, cleared at once.
  const [billingReturn] = useState(() => takeBillingReturn());

  const openTailor = (from: View) => (jobId: string) => {
    setTailor({ jobId, from });
    setView("tailor");
  };

  /**
   * One application, found: the Applications page opened scrolled to that
   * row and lit up. "See Applications" used to land at the top of the page
   * and leave the person hunting for the row a cloud run had just stopped on.
   */
  const [focusJob, setFocusJob] = useState<string | null>(null);
  const openApplication = (jobId: string) => {
    setFocusJob(jobId);
    setView("applications");
  };
  /** One application's open questions, and nothing else (AnswerLeft): what a needs-you row's "Answer what's left" opens. */
  const [answerJob, setAnswerJob] = useState<string | null>(null);
  const openAnswerLeft = (jobId: string) => {
    setAnswerJob(jobId);
    setView("answer-left");
  };
  // #application=<job id> lands on that row once signed in: what a click on
  // the extension's note ("JobCork needs you: … at …") opens. The hash is
  // read once and cleared, so a reload does not jump again.
  const [linkedJob, setLinkedJob] = useState(() => /^#application=([0-9a-f-]{36})$/i.exec(window.location.hash)?.[1] ?? null);
  useEffect(() => {
    if (phase.k !== "in" || !linkedJob) return;
    openApplication(linkedJob);
    setLinkedJob(null);
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
  // Once, when the sign-in settles; openApplication is a stable pair of setters.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase.k, linkedJob]);

  /** Re-reads the checklist after a screen saves, so progress is never stale. */
  const refreshMe = useCallback(async () => {
    let email: string | null = null;
    try { email = (await (await getAuthClient()).getUser())?.email ?? null; } catch { /* token claim is the fallback */ }
    const me = await api<Me>("/api/me", { method: "POST", body: JSON.stringify({ email }) });
    setPhase({ k: "in", me });
  }, []);

  // The API answered 402 "subscription required" somewhere mid-session (a
  // subscription lapsed, or the founder period ended while this tab was
  // open). Re-read /api/me, which puts the subscribe screen up.
  useEffect(() => {
    const onGate = () => { void refreshMe().catch(() => { /* the screen it was on shows the error */ }); };
    window.addEventListener("jobcork:subscription-required", onGate);
    return () => window.removeEventListener("jobcork:subscription-required", onGate);
  }, [refreshMe]);

  /**
   * Wraps a click handler so a rejected promise surfaces on screen instead of
   * becoming an unhandled rejection nobody sees without devtools open.
   */
  const guard = (fn: () => Promise<unknown>) => () => {
    setActionError(null);
    fn().catch((err) => setActionError(describe(err)));
  };

  /**
   * Every await here can hang rather than reject: a token refresh against an
   * unreachable Auth0, or a fetch proxied to an API server that is not
   * running. Without a deadline the app sits on "Loading…" forever with no
   * indication of what it is waiting for — the same swallowed-failure shape
   * that has bitten this codebase repeatedly, just spelled differently.
   */
  const start = useCallback(() => {
    setPhase({ k: "loading" });
    const deadline = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error(
        "Startup timed out after 15 seconds. The API server is probably not running — check the terminal running `pnpm dev` in apps/api.",
      )), 15_000),
    );

    Promise.race([
      (async () => {
        await handleRedirect();
        const s = await getSession();
        if (!s) return { k: "signed-out" } as const;
        setSession(s);
        return { k: "in", me: await api<Me>("/api/me", { method: "POST", body: JSON.stringify({ email: s.user.email ?? null }) }) } as const;
      })(),
      deadline,
    ])
      .then(setPhase)
      // A ReauthError means the page is already on its way to Auth0 and back;
      // "Something went wrong" for the half-second before it leaves is wrong.
      .catch((err) => { if (!(err instanceof ReauthError)) setPhase({ k: "error", msg: describe(err) }); });
  }, []);

  useEffect(start, [start]);

  // Hand the extension the sign-in, and keep answering it, for as long as the
  // person is signed in on this page. The extension never talks to Auth0.
  const signedIn = phase.k === "in";
  useEffect(() => {
    if (!signedIn || !session) return;
    return connectExtension(
      async () => tokenOrRelogin(await getAuthClient()),
      { name: session.user.name ?? null, email: session.user.email ?? null },
      setExt,
    );
  }, [signedIn, session]);

  const signOut = async () => {
    notifyExtensionSignedOut();
    await logout();
  };

  if (phase.k === "loading") {
    return (
      <Shell>
        <p className="muted">Loading…</p>
        <p className="muted small">
          If this does not clear in a few seconds, the API server is probably
          not running.
        </p>
      </Shell>
    );
  }

  if (phase.k === "error") {
    return (
      <Shell>
        <h1>Something went wrong</h1>
        <p className="err">{phase.msg}</p>
        <p className="muted small">
          If this mentions the database, the API server may need restarting.
          If it mentions Auth0, the message above names the setting to check.
        </p>
        <div className="row">
          <button className="btn" onClick={start}>Try again</button>
          <button className="btn ghost" onClick={guard(signOut)}>Sign out</button>
        </div>
      </Shell>
    );
  }

  if (phase.k === "signed-out") {
    return <Landing onSignup={guard(signup)} onLogin={guard(login)} error={actionError} brand={<Brand size={44} />} />;
  }

  const { me } = phase;
  // A plain nav click to Matches shows all routes; only the dashboard breakdown deep-links to one.
  const navigate = (v: View) => { if (v === "matches") setMatchesRoute(null); setView(v); };
  const bar = {
    who: me.email ?? me.sub,
    onSignOut: guard(signOut),
    nav: <Nav view={view} onNav={navigate} />,
    bottomNav: <BottomNav view={view} onNav={navigate} />,
    switcher: me.tracks && me.tracks.length > 0
      ? <TrackSwitcher tracks={me.tracks} onManage={() => setView("tracks")} />
      : null,
  };

  // The paywall (jobcork-pricing.md): billing is on and this person is neither
  // a founder in the founder period nor a subscriber. The bar stays -- who they
  // are, and sign out -- the product does not.
  if (me.access?.enabled && !me.access.allowed) {
    return (
      <Shell bar={{ who: bar.who, onSignOut: bar.onSignOut }}>
        <SubscribeScreen returned={billingReturn} onAllowed={() => guard(refreshMe)()} />
      </Shell>
    );
  }

  if (previewLanding) {
    const leave = () => { history.replaceState(null, "", window.location.pathname); setPreviewLanding(false); setView("dashboard"); };
    return <Landing onSignup={leave} onLogin={leave} error={null} brand={<Brand size={44} />} signedIn />;
  }

  // The setup, in order, and the step that comes after the one on screen:
  // the next thing still to do, or the matches when nothing is. Every setup
  // screen ends with it, so nobody is left at the bottom of a form.
  const setup: Array<{ go: View; done: boolean; label: string; hint: string }> = [
    { go: "resume", done: me.onboarding.resumeUploaded, label: "Upload your resume", hint: "We pull out the text and show it to you; you correct it and confirm it." },
    { go: "preferences", done: me.onboarding.preferencesSet, label: "Set what you're looking for", hint: "Titles, places, pay floor. Obeyed exactly." },
    { go: "answers", done: me.onboarding.answersConfirmed, label: "Confirm your application answers", hint: "Work authorization and the rest. Nothing is applied to until these are confirmed." },
  ];
  const nextAfter = (current: View): NextStepInfo => {
    const idx = setup.findIndex((s) => s.go === current);
    const ordered = idx >= 0 ? [...setup.slice(idx + 1), ...setup.slice(0, idx)] : setup;
    const pending = ordered.find((s) => !s.done);
    if (pending) return { go: pending.go, label: pending.label, hint: pending.hint };
    return { go: "matches", label: "See your ranked matches", hint: "Ranked against your resume and your targets, with the reasoning shown for each one." };
  };
  const goTo = (go: string) => setView(go as View);

  if (view === "resume") {
    return (
      <Shell bar={bar}>
        <ResumeScreen onSaved={refreshMe} onBack={() => setView("dashboard")} next={nextAfter("resume")} onGo={goTo} />
      </Shell>
    );
  }
  if (view === "tracks") {
    return (
      <Shell bar={bar}>
        <TracksScreen onChanged={refreshMe} onBack={() => setView("dashboard")} />
      </Shell>
    );
  }
  if (view === "auto-apply") {
    return (
      <Shell bar={bar}>
        <AutoApply onSaved={refreshMe} onBack={() => setView("dashboard")} onGo={goTo} />
      </Shell>
    );
  }
  if (view === "settings") {
    return (
      <Shell bar={bar}>
        <Settings onBack={() => setView("dashboard")} onGo={goTo} onSignOut={signOut} />
      </Shell>
    );
  }
  if (view === "guide") {
    return (
      <Shell bar={bar}>
        <Guide onBack={() => setView("dashboard")} onGo={goTo} ext={ext} />
      </Shell>
    );
  }
  if (view === "preferences") {
    return (
      <Shell bar={bar}>
        <Preferences onSaved={refreshMe} onBack={() => setView("dashboard")} next={nextAfter("preferences")} onGo={goTo} savedBefore={me.onboarding.preferencesSet} />
      </Shell>
    );
  }
  if (view === "answers") {
    return (
      <Shell bar={bar}>
        <AnswersScreen onSaved={refreshMe} onBack={() => setView("dashboard")} next={nextAfter("answers")} onGo={goTo} />
      </Shell>
    );
  }
  if (view === "matches") {
    return (
      <Shell bar={bar}>
        <Matches
          onBack={() => { setView("dashboard"); guard(refreshMe)(); }}
          onOpenApplications={() => setView("applications")}
          onOpenPreferences={() => setView("preferences")}
          onTailor={openTailor("matches")}
          ext={ext}
          auto={me.auto ?? null}
          onAutoChanged={() => guard(refreshMe)()}
          initialRoute={matchesRoute}
        />
      </Shell>
    );
  }
  if (view === "applications") {
    return (
      <Shell bar={bar}>
        <Applications
          onBack={() => { setView("dashboard"); guard(refreshMe)(); }}
          onOpenMatches={() => navigate("matches")}
          onTailor={openTailor("applications")}
          autoMode={me.auto?.mode ?? "review"}
          ext={ext}
          focusJobId={focusJob}
          onFocused={() => setFocusJob(null)}
          onAnswerLeft={openAnswerLeft}
        />
      </Shell>
    );
  }
  if (view === "answer-left" && answerJob) {
    return (
      <Shell bar={bar}>
        <AnswerLeft jobId={answerJob} autoMode={me.auto?.mode ?? "review"} onBack={() => { setView("dashboard"); guard(refreshMe)(); }} onGo={goTo} onReview={openApplication} />
      </Shell>
    );
  }
  if (view === "tailor" && tailor) {
    return (
      <Shell bar={bar}>
        <TailorScreen jobId={tailor.jobId} onBack={() => setView(tailor.from)} />
      </Shell>
    );
  }

  const steps: Array<{ done: boolean; label: string; hint: string; go?: View }> = [
    { done: me.onboarding.resumeUploaded, label: "Upload your resume", hint: "We'll pull out your history — you get to correct it.", go: "resume" },
    { done: me.onboarding.preferencesSet, label: "Set what you're looking for", hint: "Titles, places, pay floor. We obey these exactly.", go: "preferences" },
    { done: me.onboarding.answersConfirmed, label: "Confirm your application answers", hint: "Work authorization and the rest. We never guess these.", go: "answers" },
    // The extension, in this browser: never done in Safari, and the hint says why.
    extensionStep(ext),
  ];
  const remaining = steps.filter((s) => !s.done).length;

  return (
    <Shell bar={bar}>
      {me.isFounder && <p className="badge">Founder member</p>}
      {billingReturn === "success" && me.access?.reason === "subscribed" && <p className="badge">You're subscribed — thank you.</p>}
      {actionError && <p className="err">{actionError}</p>}
      {fromExtension && (
        ext?.connected ? (
          <p className="badge">Extension connected as {session?.user.email ?? me.email ?? "you"} — you can close this tab.</p>
        ) : ext && !ext.installed ? (
          <p className="err">The JobCork extension is not installed in this browser, or is disabled. Install it, then open this page again.</p>
        ) : (
          <p className="muted small">Connecting to the extension…</p>
        )
      )}

      <Dashboard
        me={me}
        ext={ext}
        onView={(v, route) => { if (v === "matches") setMatchesRoute(route ?? null); setView(v); }}
        onTailor={openTailor("dashboard")}
        onReview={openApplication}
        onAnswerLeft={openAnswerLeft}
      />

      <section>
        <h2>{remaining === 0 ? "You're set up" : `${remaining} thing${remaining > 1 ? "s" : ""} left`}</h2>
        <ol className="steps">
          {steps.map((s) => (
            <li key={s.label} className={s.done ? "done" : ""}>
              <span className="mark" aria-hidden="true">{s.done ? "✓" : ""}</span>
              <div>
                {s.go ? (
                  // A finished step stays reachable. People revise these, and a
                  // checklist that locks you out once you tick a box is hostile.
                  <button className="step-link" onClick={() => setView(s.go!)}>
                    {s.label}
                  </button>
                ) : (
                  <p className="step-label">{s.label}</p>
                )}
                <p className="muted small">{s.hint}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>


    </Shell>
  );
}

/** The pushpin: the mark, and the only red on any screen. */
/**
 * "JobCork was updated": shown when a newer build has been deployed than
 * the one running, with a reload button. Checked on a timer and whenever
 * the page regains focus. Never reloads by itself.
 */
function UpdateBar() {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let alive = true;
    const check = () => { void updateAvailable().then((v) => { if (alive && v) setReady(true); }); };
    const t = setInterval(check, UPDATE_CHECK_MS);
    const onFocus = () => check();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    const first = setTimeout(check, 20_000);
    return () => { alive = false; clearInterval(t); clearTimeout(first); window.removeEventListener("focus", onFocus); document.removeEventListener("visibilitychange", onFocus); };
  }, []);
  if (!ready) return null;
  return (
    <div className="update-bar" role="status">
      <span>JobCork was updated since this page loaded.</span>
      <button className="btn small" onClick={() => window.location.reload()}>Reload to get the latest</button>
    </div>
  );
}

/**
 * The mark: a card pinned to a board -- the cork board the name is short
 * for, in the brand's own colours (the app's board, its paper, its one red).
 * Drawn once here and used everywhere: the top bar, the front door, the
 * static pages' header (public/mark.svg is the same drawing).
 */
export function Mark({ size = 24 }: { size?: number }) {
  return (
    <svg className="brand-mark" width={size} height={size} viewBox="0 0 44 44" aria-hidden="true">
      <rect x="1.5" y="1.5" width="41" height="41" rx="10" fill="#1c2230" stroke="#4a5568" strokeWidth="3" />
      <g transform="rotate(-7 22 27)">
        <rect x="9" y="14" width="26" height="27" rx="3" fill="#ffffff" />
        <rect x="13.5" y="24.5" width="17" height="3.2" rx="1.6" fill="#b9c0cc" />
        <rect x="13.5" y="31" width="12" height="3.2" rx="1.6" fill="#b9c0cc" />
      </g>
      <path d="M22 17 L22 23.5" stroke="#14181f" strokeWidth="3" strokeLinecap="round" opacity="0.6" />
      <circle cx="22" cy="12" r="7" fill="#d9382f" />
      <circle cx="19.4" cy="9.4" r="2.2" fill="#ffffff" opacity="0.55" />
    </svg>
  );
}

function Brand({ size = 24 }: { size?: number }) {
  return (
    <span className="brand" aria-label="JobCork">
      <Mark size={size} />
      <span className="brand-name">JobCork</span>
    </span>
  );
}

/**
 * Every screen sits on the same 640px column. Signed-in screens carry the
 * board-coloured top bar -- brand, who is signed in, sign out -- so no screen
 * has to draw its own; signed-out and error states do not, because there is
 * no one to name.
 */
/**
 * The persistent navigation, on the board bar. This is the Signpost change:
 * every part of the app has a home you can reach from anywhere, instead of a
 * feature being buried inside whichever screen it was built into.
 */
const NAV_ITEMS: Array<{ v: View; label: string }> = [
  { v: "dashboard", label: "Dashboard" },
  { v: "matches", label: "Matches" },
  { v: "applications", label: "Applications" },
  { v: "auto-apply", label: "Auto-apply" },
  { v: "guide", label: "Guide" },
  { v: "settings", label: "Settings" },
];

/** Which nav item is lit for the current view; some screens live under Settings. */
function navActiveFor(view: View): View {
  if (view === "resume" || view === "answers" || view === "preferences" || view === "tracks") return "settings";
  if (view === "tailor") return "matches";
  return view;
}

function Nav({ view, onNav }: { view: View; onNav: (v: View) => void }) {
  const active = navActiveFor(view);
  return (
    <nav className="topnav" aria-label="Primary">
      {NAV_ITEMS.map((it) => (
        <button
          key={it.v}
          className={`topnav-link${active === it.v ? " on" : ""}`}
          aria-current={active === it.v ? "page" : undefined}
          onClick={() => onNav(it.v)}
        >
          {it.label}
        </button>
      ))}
    </nav>
  );
}

/* Phone navigation icons — stroke follows currentColor so the active tab reddens. */
const navIconProps = { width: 20, height: 20, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round" as const, strokeLinejoin: "round" as const, "aria-hidden": true };
function IconHome() { return <svg {...navIconProps}><path d="M3 10.5 12 3l9 7.5" /><path d="M5 9.5V21h14V9.5" /></svg>; }
function IconSearch() { return <svg {...navIconProps}><circle cx="11" cy="11" r="7" /><path d="m20 20-3-3" /></svg>; }
function IconList() { return <svg {...navIconProps}><path d="M4 6h16M4 12h16M4 18h10" /></svg>; }
function IconBolt() { return <svg {...navIconProps}><path d="M13 2 4 14h7l-1 8 9-12h-7z" /></svg>; }
function IconMore() { return <svg {...navIconProps}><circle cx="5" cy="12" r="1.4" /><circle cx="12" cy="12" r="1.4" /><circle cx="19" cy="12" r="1.4" /></svg>; }

const BOTTOM_ITEMS: Array<{ v: View; label: string; icon: React.ReactNode }> = [
  { v: "dashboard", label: "Home", icon: <IconHome /> },
  { v: "matches", label: "Matches", icon: <IconSearch /> },
  { v: "applications", label: "Apps", icon: <IconList /> },
  { v: "auto-apply", label: "Auto", icon: <IconBolt /> },
];

/**
 * The phone navigation. The top-bar nav row reads well on a wide screen but is
 * cramped on a phone, so at phone width the top nav hides (CSS) and this fixed
 * bottom bar takes over -- the pattern the approved mock shows and every phone
 * app uses. Four taps for the main screens, plus "More" for Guide and Settings,
 * which do not earn a permanent tab. Rendered always; shown only at phone width.
 */
function BottomNav({ view, onNav }: { view: View; onNav: (v: View) => void }) {
  const [moreOpen, setMoreOpen] = useState(false);
  const active = navActiveFor(view);
  const moreActive = active === "settings" || active === "guide";
  const go = (v: View) => { setMoreOpen(false); onNav(v); };
  return (
    <nav className="bottomnav" aria-label="Primary">
      {moreOpen && <button className="bottomnav-scrim" aria-label="Close menu" onClick={() => setMoreOpen(false)} />}
      {BOTTOM_ITEMS.map((it) => {
        const on = active === it.v;
        return (
          <button
            key={it.v}
            className={`bnav${on ? " on" : ""}`}
            aria-current={on ? "page" : undefined}
            onClick={() => go(it.v)}
          >
            <span className="bnav-ic">{it.icon}</span>
            {it.label}
          </button>
        );
      })}
      <button
        className={`bnav${moreActive || moreOpen ? " on" : ""}`}
        aria-haspopup="true"
        aria-expanded={moreOpen}
        onClick={() => setMoreOpen((v) => !v)}
      >
        <span className="bnav-ic"><IconMore /></span>
        More
      </button>
      {moreOpen && (
        <div className="bottomnav-more" role="menu">
          <button className="bnav-more-item" role="menuitem" onClick={() => go("guide")}>Guide</button>
          <button className="bnav-more-item" role="menuitem" onClick={() => go("settings")}>Settings</button>
        </div>
      )}
    </nav>
  );
}

function Shell({ children, bar }: { children: React.ReactNode; bar?: { who: string; onSignOut: () => void; switcher?: React.ReactNode; nav?: React.ReactNode; bottomNav?: React.ReactNode } }) {
  return (
    <>
      <UpdateBar />
      {bar && (
        <div className="topbar">
          <div className="topbar-in">
            <Brand size={34} />
            {bar.nav}
            <div className="topbar-right">
              {bar.switcher}
              <span className="who">{bar.who}</span>
              <button className="btn ghost small" onClick={bar.onSignOut}>Sign out</button>
            </div>
          </div>
        </div>
      )}
      <main className="wrap">{children}</main>
      {bar && bar.bottomNav}
    </>
  );
}
